import memoize from 'lodash/memoize';
import {
  challengeAPI,
  participantAPI,
} from '@wix/challenges-web-api/dist/src/API';
import {
  FeedbackItem,
  ListParticipantStepsResponse,
  ParticipantSection,
  ParticipantState,
  ParticipantStepStateTransition,
  V1DurationUnit,
  V1ParticipantStep,
} from '@wix/ambassador-challenge-service-web/types';
import { PARTICIPANT_STEPS } from '../../__mocks__/participantSteps';
import { ControllerFlowAPI } from 'yoshi-flow-editor-runtime/build/cjs/flow-api/ViewerScript';
import { IParticipantStepsContext } from './ParticipantStepsContext';
import { userProviderPropsMap } from '../User/userProviderPropsMap';
import userTypeHandlers from '../User/helpers/userTypeHandlers';
import {
  getChallengeWeekRangeForDate,
  getFirstDayOfChallenge,
} from '../../selectors/dates';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import add from 'date-fns/add';
import differenceInCalendarWeeks from 'date-fns/differenceInCalendarWeeks';
import { challengesListDataProviderPropsMap } from '../ChallengesListDataProvider/challengesListDataProviderPropsMap';
import { toParticipantSteps } from './toParticipantSteps';
import { handleError } from '../ErrorHandler/errorHandlerPropsMap';
import { isForcedPreviewParticipant } from '../../selectors/isForcedPreview';
import { syncInstance } from '../../services/instance';
import { getChallengeId } from '../ChallengeDataProvider/challengeDataProviderPropsMap';
import { isMockedChallenge } from '../main/getMockedChallenges';

function handleUserLogin(flowAPI: ControllerFlowAPI) {
  flowAPI.controllerConfig.wixCodeApi.user.onLogin(async () => {
    flowAPI.controllerConfig.setProps({
      participantSteps: await loadParticipantSteps(flowAPI),
    });
  });
}

function getOffset(
  selectedDate: Date,
  includeAll: boolean,
): { from: number; to: number } {
  if (includeAll) {
    return {
      from: 0,
      to: 365,
    };
  }
  if (!selectedDate) {
    return { from: 0, to: 6 };
  }

  const weekCount = differenceInCalendarWeeks(selectedDate, new Date());

  if (!weekCount) {
    return { from: 0, to: 6 };
  }

  return {
    from: weekCount * 6 + weekCount,
    to: weekCount * 7 + 6,
  };
}

const loadParticipantSteps = async (
  flowAPI: ControllerFlowAPI,
  currentDate: Date = null,
  includeAll: boolean = false,
): Promise<IParticipantStepsContext['participantSteps']> => {
  const { isEditor, isPreview } = flowAPI.environment;
  const userProvider = await userProviderPropsMap(flowAPI);
  const { participant } = userProvider;

  syncInstance(flowAPI);

  const isJoinedParticipant =
    participant?.id &&
    userTypeHandlers.isJoinedAlready(participant?.transitions?.[0]?.state);

  let participantSteps: ListParticipantStepsResponse = {
    steps: [],
  };

  flowAPI.controllerConfig.setProps({
    isParticipantStepsLoading: true,
  });

  if (
    isJoinedParticipant &&
    userProvider.userType !== ParticipantState.RUNNING
  ) {
    const weekRange = getChallengeWeekRangeForDate(
      participant.dateFrame,
      currentDate ? new Date(currentDate) : new Date(),
      flowAPI?.reportError,
    );

    try {
      const challengeId = await getChallengeId(flowAPI);

      if (!challengeId) {
        return;
      }

      participantSteps = await participantAPI.listSteps({
        challengeId,
        dateInterval: {
          start: includeAll
            ? format(
                getFirstDayOfChallenge(participant.dateFrame),
                'yyyy-MM-dd',
              )
            : weekRange.from
            ? format(weekRange.from, 'yyyy-MM-dd')
            : null,
          finish: includeAll
            ? `${format(
                add(new Date(), {
                  years: 2,
                }),
                'yyyy',
              )}-01-01` // todo: remove after backend fix
            : weekRange.to
            ? format(addDays(weekRange.to, 1), 'yyyy-MM-dd')
            : null,
        },
        participantId: participant.id,
      });
    } catch (error) {
      handleError({ error, context: 'participantAPI.listSteps' });
    }
  }

  if (
    (isEditor ||
      isPreview ||
      isForcedPreviewParticipant(
        flowAPI?.controllerConfig?.wixCodeApi?.location?.query,
      )) &&
    !participantSteps?.steps?.length
  ) {
    const challengeId =
      (await getChallengeId(flowAPI)) ||
      (await challengesListDataProviderPropsMap(flowAPI)).challengesListData
        ?.memberChallenges?.[0]?.challenge?.id;

    // for editor preview need to calculate correct range in scheduled challenge
    const offset = getOffset(currentDate, includeAll);

    let steps;

    if (challengeId && !isMockedChallenge(challengeId, flowAPI)) {
      try {
        const challengeSteps = await challengeAPI.listSteps({
          challengeId,
          fromOffset: { value: offset.from, unit: V1DurationUnit.DAYS },
          toOffset: { value: offset.to, unit: V1DurationUnit.DAYS },
        });

        steps = toParticipantSteps(challengeSteps);
      } catch (error) {
        handleError({ error, context: 'getOwnerListSteps' });
      }
    }

    participantSteps = {
      steps: steps || (PARTICIPANT_STEPS as any),
    };
  }

  flowAPI.controllerConfig.setProps({
    isParticipantStepsLoading: false,
  });

  return participantSteps;
};

const updateParticipantStepStatus = (
  flowAPI: ControllerFlowAPI,
  sections: ParticipantSection[],
  steps: V1ParticipantStep[],
  stepId: string,
  transitions?: ParticipantStepStateTransition[],
  feedbackItems?: FeedbackItem[],
): void => {
  const checkStep = (step) => {
    if (step.id === stepId) {
      if (transitions) {
        step.transitions = transitions;
      }
      if (feedbackItems) {
        step.feedback = {
          items: feedbackItems,
        };
      }
    }
  };

  if (sections?.length) {
    sections.forEach((section) => {
      (section.steps || []).forEach(checkStep);
    });

    flowAPI.controllerConfig.setProps({
      listParticipantSections: sections,
    });
  } else if (steps?.length) {
    steps.forEach(checkStep);

    flowAPI.controllerConfig.setProps({
      participantSteps: { steps },
    });
  } else {
    console.error("Can't update participant step status.");
  }
};

export const participantStepsDataProviderPropsMap = memoize(async function (
  flowAPI: ControllerFlowAPI,
): Promise<IParticipantStepsContext> {
  handleUserLogin(flowAPI);

  const isSidebarLayout =
    (flowAPI.controllerConfig.config?.publicData as any)?.COMPONENT
      ?.participantLayout === 'SIDEBAR';

  return {
    participantSteps: await loadParticipantSteps(
      flowAPI,
      null,
      isSidebarLayout,
    ),
    updateParticipantSteps: async (
      currentDate: Date = null,
      includeAll: boolean = false,
    ) => {
      flowAPI.controllerConfig.setProps({
        participantSteps: await loadParticipantSteps(
          flowAPI,
          currentDate,
          includeAll || isSidebarLayout, // we need always load all steps for sidebar layout until we add paging
        ),
      });
    },
    isParticipantStepsLoading: false,
    updateParticipantStepStatus: async (
      sections,
      steps,
      stepId,
      transitions?,
      feedbackItems?,
    ) =>
      updateParticipantStepStatus(
        flowAPI,
        sections,
        steps,
        stepId,
        transitions,
        feedbackItems,
      ),
  };
});
